import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Calendar, dateFnsLocalizer, type SlotInfo, Views } from 'react-big-calendar';
import withDragAndDrop, {
  type EventInteractionArgs,
} from 'react-big-calendar/lib/addons/dragAndDrop';
import {
  type AppointmentUpdate,
  setEditingAppointmentId,
  useAppointments,
} from '@features/appointments';
import { useAppDispatch } from '@store/hooks';
import { format, getDay, parse, startOfWeek } from 'date-fns';
import { enUS } from 'date-fns/locale/en-US';

import AddAppointmentModal from '../AddAppointmentModal';

import CalendarContainer from './components/CalendarContainer';
import CalendarLoadingOverlay from './components/CalendarLoadingOverlay';
import {
  CustomHeader,
  CustomMonthHeader,
  CustomTimeGutterHeader,
} from './components/CustomHeaders';
import { EventComponent } from './components/EventComponent';
import { type CalendarEvent } from './types';
import {
  DEFAULT_ENCOUNTER_DURATION,
  getAppointmentDisplayTitle,
  getBigCalendarView,
  VALID_STEPS,
  VALID_TIMESLOTS,
} from './utils';

import '../../styles/calendar-custom.scss';
import 'react-big-calendar/lib/addons/dragAndDrop/styles.css';

// Setup the localizer for the calendar
const locales = {
  'en-US': enUS,
};

const localizer = dateFnsLocalizer({
  format,
  parse,
  startOfWeek,
  getDay,
  locales,
});

// Create a drag and drop enabled calendar with proper typing
const DnDCalendar = withDragAndDrop<CalendarEvent>(Calendar);

interface CalendarWidgetProps {
  onAppointmentUpdate: (
    appointmentId: string,
    updates: AppointmentUpdate,
    seriesId?: string,
  ) => void;
}

const CalendarWidget: React.FC<CalendarWidgetProps> = ({ onAppointmentUpdate }) => {
  // Get encounters data from context
  const {
    appointments,
    isLoading,
    isBackgroundLoading,
    viewMode,
    setViewMode,
    currentDate,
    setCurrentDate,
    calendarGranularity,
    refreshAppointments,
  } = useAppointments();

  const dispatch = useAppDispatch();

  const [events, setEvents] = useState<CalendarEvent[]>([]);

  // Validate and sanitize calendar granularity to prevent invalid array length
  const safeStep = useMemo(() => {
    const step = calendarGranularity?.step;
    return VALID_STEPS.includes(step) ? step : 15; // Default to 15 if invalid
  }, [calendarGranularity?.step]);

  const safeTimeslots = useMemo(() => {
    const timeslots = calendarGranularity?.timeslots;
    return VALID_TIMESLOTS.includes(timeslots) ? timeslots : 4; // Default to 4 if invalid
  }, [calendarGranularity?.timeslots]);

  // Set initial time to scroll to
  const scrollToTime = new Date();
  scrollToTime.setHours(scrollToTime.getHours() - 2, 0, 0); // 2 hours before current time

  // State for AddAppointmentModal
  const [isAddAppointmentModalOpen, setIsAddAppointmentModalOpen] = useState(false);
  const [selectedDateTime, setSelectedDateTime] = useState<Date | undefined>(undefined);
  const [selectedDuration, setSelectedDuration] = useState<number | undefined>(undefined);

  // Convert encounters to calendar events when encounters data changes
  useEffect(() => {
    const calendarEvents = appointments.map((appointment) => {
      // Use start_time from new model
      const startTime = new Date(appointment.start_time);

      // Calculate end time based on duration
      const endTime = new Date(startTime);

      // Use duration from new model or default
      const durationMinutes = appointment.duration || DEFAULT_ENCOUNTER_DURATION;
      endTime.setMinutes(endTime.getMinutes() + durationMinutes);

      return {
        id: appointment.appointment_id,
        title: getAppointmentDisplayTitle(appointment),
        start: startTime,
        end: endTime,
        resource: appointment,
      };
    });

    setEvents(calendarEvents);
  }, [appointments]);

  // Handle calendar date navigation - this is now handled by CalendarNavigation component
  const handleNavigate = useCallback(
    (newDate: Date) => {
      setCurrentDate(newDate.toISOString());
    },
    [setCurrentDate],
  );

  // Handle event movement (reschedule)
  const moveEvent = useCallback(
    async ({ event, start, end }: EventInteractionArgs<CalendarEvent>) => {
      try {
        const newStartTime = new Date(start);

        // Event has been moved - newStartTime contains the updated time

        // Update local state first for immediate feedback (optimistic update)
        const updatedEvents = events.map(existingEvent => {
          if (existingEvent.id === event.id) {
            return {
              ...existingEvent,
              start: new Date(start),
              end: new Date(end),
            };
          }
          return existingEvent;
        });

        setEvents(updatedEvents);

        // Prepare update data
        const updates: AppointmentUpdate = {
          start_time: newStartTime.toISOString(),
          duration: event.resource.duration,
        };

        // Always use onAppointmentUpdate with optional seriesId
        onAppointmentUpdate(event.id, updates, event.resource.series?.series_id);
      } catch (error) {
        console.error('Error updating encounter schedule:', error);
        // If there's an error, do a full refresh to ensure the UI is in sync with server
        await refreshAppointments();
      }
    },
    [events, refreshAppointments, onAppointmentUpdate],
  );

  // Handle event resize
  const resizeEvent = useCallback(
    async ({ event, start, end }: EventInteractionArgs<CalendarEvent>) => {
      try {
        // Calculate new duration in minutes
        const newStartTime = new Date(start);
        const newEndTime = new Date(end);
        const durationMinutes = Math.round(
          (newEndTime.getTime() - newStartTime.getTime()) / (1000 * 60),
        );

        // Update local state first for immediate feedback (optimistic update)
        const updatedEvents = events.map(existingEvent => {
          if (existingEvent.id === event.id) {
            return {
              ...existingEvent,
              start: new Date(start),
              end: new Date(end),
            };
          }
          return existingEvent;
        });

        setEvents(updatedEvents);

        // Prepare update data
        const updates: AppointmentUpdate = {
          start_time: newStartTime.toISOString(),
          duration: durationMinutes,
        };

        // Always use onAppointmentUpdate with optional seriesId
        onAppointmentUpdate(event.id, updates, event.resource.series?.series_id);
      } catch (error) {
        console.error('Error updating encounter duration:', error);
        // If there's an error, do a full refresh to ensure the UI is in sync with server
        await refreshAppointments();
      }
    },
    [events, refreshAppointments, onAppointmentUpdate],
  );

  const handleClickEvent = useCallback(
    (event: CalendarEvent) => {
      // Dispatch Redux action to open edit modal
      dispatch(setEditingAppointmentId(event.resource.appointment_id));
    },
    [dispatch],
  );

  // Wrapper for EventComponent with handlers
  const EventComponentWrapper = useCallback(
    ({ event, title }: { event: CalendarEvent; title: React.ReactNode }) => {
      return <EventComponent event={event} title={title} viewMode={viewMode} />;
    },
    [viewMode],
  );

  // Handle selection slot (click and drag will capture start and end times)
  const handleSelectSlot = useCallback((slotInfo: SlotInfo) => {
    const startDay = new Date(slotInfo.start).setHours(0, 0, 0, 0);
    const endDay = new Date(slotInfo.end).setHours(0, 0, 0, 0);

    // If selection spans multiple days, ignore it
    if (slotInfo.slots.length > 1 && startDay !== endDay) {
      return;
    }

    // Set the start time
    setSelectedDateTime(slotInfo.start);

    // Calculate duration in minutes if there's a difference between start and end
    if (slotInfo.start && slotInfo.end) {
      const durationMinutes = Math.round(
        (slotInfo.end.getTime() - slotInfo.start.getTime()) / (1000 * 60),
      );

      // Only set duration if it's actually a drag selection (more than a few minutes)
      if (durationMinutes > 5) {
        setSelectedDuration(durationMinutes);
      } else {
        setSelectedDuration(undefined);
      }
    }

    setIsAddAppointmentModalOpen(true);
  }, []);

  // Handle close of AddAppointmentModal
  const handleCloseAddAppointmentModal = useCallback(() => {
    setIsAddAppointmentModalOpen(false);
    setSelectedDateTime(undefined);
    setSelectedDuration(undefined);
  }, []);

  // Add handler for encounter creation
  const handleAppointmentCreated = useCallback(() => {
    // Refresh encounters to show the new one
    refreshAppointments();
  }, [refreshAppointments]);

  // eventPropGetter removed - EventComponent now owns all styling

  return (
    <CalendarContainer>
      <CalendarLoadingOverlay isLoading={isLoading} isBackgroundLoading={isBackgroundLoading} />

      <DnDCalendar
        localizer={localizer}
        events={events}
        defaultView={getBigCalendarView(viewMode)}
        view={getBigCalendarView(viewMode)}
        views={[Views.DAY, Views.WEEK, Views.MONTH]}
        step={safeStep}
        showMultiDayTimes
        date={currentDate}
        onNavigate={handleNavigate}
        onView={setViewMode}
        scrollToTime={scrollToTime}
        onSelectEvent={handleClickEvent}
        onEventDrop={moveEvent}
        onEventResize={resizeEvent}
        selectable={true}
        onSelectSlot={handleSelectSlot}
        resizable={true}
        resizableAccessor={() => viewMode !== 'month'}
        tooltipAccessor={null} // Disable native browser tooltip (ExpandedCard shows details)
        style={{ height: '100%' }}
        timeslots={safeTimeslots}
        dayLayoutAlgorithm="no-overlap"
        toolbar={false} // Hide the default toolbar since we have our own navigation
        components={{
          event: EventComponentWrapper,
          week: {
            header: CustomHeader,
          },
          month: {
            header: CustomMonthHeader,
          },
          ...(viewMode === 'day' && {
            timeGutterHeader: () => <CustomTimeGutterHeader currentDate={currentDate} />,
          }),
        }}
      />

      {/* Add Appointment Modal */}
      <AddAppointmentModal
        open={isAddAppointmentModalOpen}
        onClose={handleCloseAddAppointmentModal}
        initialScheduledTime={selectedDateTime}
        initialDuration={selectedDuration}
        onAppointmentCreated={handleAppointmentCreated}
      />
    </CalendarContainer>
  );
};

export default CalendarWidget;
